import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { publicFormsApi, publicFormFeatureError } from '../services/publicFormsApi';
import {
    isReusablePublicForm,
    parsePublicFormRequestFieldConfig,
} from '../utils/matterPublicForms';
import { publicFormTargetLabel } from '../utils/publicFormBuilder';
import { Checkbox } from './FieldComponents';
import '../styles/PublicFormRequestFieldSettings.css';

const PublicFormRequestFieldSettings = ({ value, onChange, caseTypeId }) => {
    const config = useMemo(() => parsePublicFormRequestFieldConfig(value), [value]);
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError('');
        publicFormsApi.list({ signal: controller.signal })
            .then((payload) => setForms(Array.isArray(payload.forms) ? payload.forms : []))
            .catch((requestError) => {
                if (requestError?.name !== 'AbortError') setError(publicFormFeatureError(requestError));
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, []);

    const eligibleForms = useMemo(() => forms.filter((form) => (
        isReusablePublicForm(form)
        && (!Number(caseTypeId) || Number(form.case_type_id) === Number(caseTypeId))
    )), [caseTypeId, forms]);
    const selectedFormAvailable = eligibleForms.some((form) => Number(form.id) === Number(config.form_id));

    const update = (patch) => onChange?.({ ...config, ...patch });
    const toggleChannel = (channel) => {
        const channels = new Set(config.default_channels);
        if (channels.has(channel)) channels.delete(channel); else channels.add(channel);
        update({ default_channels: [...channels] });
    };

    return (
        <fieldset className="public-form-request-field-settings">
            <legend>Public form request</legend>
            <p className="subtext">
                This field creates a new matter-specific link and sends it from the case or lead. Every send is recorded in the activity feed.
            </p>

            <div className="form-group">
                <label htmlFor="public-form-request-form">Form</label>
                <select
                    id="public-form-request-form"
                    className="default-select"
                    value={config.form_id}
                    onChange={(event) => {
                        const formId = event.target.value ? Number(event.target.value) : '';
                        const selectedForm = eligibleForms.find((form) => Number(form.id) === Number(formId));
                        update({
                            form_id: formId,
                            target_mode: selectedForm
                                ? String(selectedForm.entity_mode || selectedForm.target_mode || '')
                                : '',
                        });
                    }}
                    disabled={loading}
                >
                    <option value="">Select a published form</option>
                    {!selectedFormAvailable && config.form_id && (
                        <option value={config.form_id}>Unavailable form #{config.form_id}</option>
                    )}
                    {eligibleForms.map((form) => (
                        <option key={form.id} value={form.id}>
                            {form.name || form.title || `Form ${form.id}`} — {publicFormTargetLabel(form.entity_mode || form.target_mode)}
                        </option>
                    ))}
                </select>
                {loading && <small className="public-form-request-settings-loading"><Loader2 size={14} className="spinner" /> Loading forms…</small>}
                {!loading && eligibleForms.length === 0 && !error && (
                    <small>No current published forms match this field’s case type. Publish one in Reports → Public forms first.</small>
                )}
                {error && <small className="public-form-request-settings-error" role="alert">{error}</small>}
            </div>

            <div className="form-group">
                <label htmlFor="public-form-request-button-label">Button text</label>
                <input
                    id="public-form-request-button-label"
                    type="text"
                    maxLength={80}
                    value={config.button_label}
                    onChange={(event) => update({ button_label: event.target.value })}
                    placeholder="Send form"
                />
            </div>

            <div className="form-group public-form-request-channel-settings">
                <label>Send through</label>
                <div>
                    <Checkbox
                        value={config.default_channels.includes('email')}
                        onChange={() => toggleChannel('email')}
                        label="Email"
                        ariaLabel="Send public form by email"
                    />
                    <Checkbox
                        value={config.default_channels.includes('sms')}
                        onChange={() => toggleChannel('sms')}
                        label="Text message"
                        ariaLabel="Send public form by text message"
                    />
                </div>
                {config.default_channels.length === 0 && <small role="alert">Select at least one delivery method.</small>}
            </div>

            <div className="public-form-request-link-settings">
                <div className="form-group">
                    <label htmlFor="public-form-request-expiration">Link lifetime in days</label>
                    <input
                        id="public-form-request-expiration"
                        type="number"
                        min="1"
                        max="3650"
                        step="1"
                        value={config.expires_in_days}
                        onChange={(event) => update({ expires_in_days: event.target.value })}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="public-form-request-max-uses">Maximum submissions</label>
                    <input
                        id="public-form-request-max-uses"
                        type="number"
                        min="1"
                        max="1000"
                        step="1"
                        value={config.max_uses}
                        disabled={config.unlimited_submissions}
                        onChange={(event) => update({ max_uses: event.target.value })}
                    />
                    <Checkbox
                        value={config.unlimited_submissions}
                        onChange={() => update({ unlimited_submissions: !config.unlimited_submissions })}
                        label="Allow unlimited submissions"
                        ariaLabel="Allow unlimited public form submissions"
                    />
                </div>
            </div>
        </fieldset>
    );
};

export default PublicFormRequestFieldSettings;
